import { StrictMode, useEffect } from 'react'
import { Switch, Route, useLocation, Router } from 'wouter'
import './index.css'
import { SWRConfig } from 'swr'
import { fetcher } from './lib/fetcher'
import { Toaster } from '@/components/ui/toaster'

import { ErrorBoundary } from './components/ErrorBoundary'
import { LoadingProvider } from './components/LoadingProvider'
import NotFound from './components/NotFound'
import { MobileProvider } from './hooks/use-mobile'
import WalletPage from './pages/WalletPage'
import MenuPage from './pages/MenuPage'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import TransactionsPage from './pages/TransactionsPage'
import SwapPage from './pages/SwapPage'
import WaiverPage from './pages/WaiverPage'
import MembershipPage from './pages/MembershipPage'
import ChatSupport from './components/ChatSupport'
import EntryPassesPage from './pages/EntryPassesPage'
import AppKitProvider from './providers/AppKitProvider'
import { UserProvider } from './providers/UserProvider'
import { getFirebaseApp, isRoleEnough, UserRole } from './constants'
import { useUser } from './hooks/use-user'
import AdminCashToTokenPage from './pages/admin/AdminCashToTokenPage'
import AdminTransactionsPage from './pages/admin/AdminTransactions'
import AdminMembershipsPage from './pages/admin/AdminMembershipsPage'
import AdminVestingManagementPage from './pages/admin/AdminVestingManagement'
import AuthActionsPage from './pages/AuthActionsPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import TermsPage from './pages/TermsPage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import { getAuth } from 'firebase/auth'
import BuyTokensPage from './pages/BuyTokensPage.tsx'
import BanxaCheckoutPage from './pages/BanxaCheckoutPage.tsx'
import NotificationsPage from './pages/NotificationsPage.tsx'
import EventsPage from './pages/EventsPage.tsx'
import NewsPage from './pages/NewsPage.tsx'
import GalleriesPage from './pages/GalleriesPage.tsx'
import ShowEventPage from './pages/ShowEventPage.tsx'
import ShowNewsPage from './pages/ShowNewsPage.tsx'
import ShowGalleryPage from './pages/ShowGalleryPage.tsx'
import AccountPage from './pages/AccountPage.tsx'

// Enhanced navigation guard with improved initial load handling
function withNavigationGuard(
	Component: React.ComponentType<any>,
	requiredRole: UserRole = UserRole.USER
) {
	return function GuardedComponent(props: any) {
		const [location, navigate] = useLocation()

		const user = useUser()

		useEffect(() => {
			if(user.loading) {
				return
			}

			if(user.id && !isRoleEnough(user.role, requiredRole)) {
				navigate('/', { replace: true })
			}

			const auth = getAuth(getFirebaseApp())

			if(!auth?.currentUser?.uid) {
				console.log('--->>>> Redirect back to login from Page guard')
				navigate('/login')
			}
		}, [user, location])

		return (
			<ErrorBoundary>
				<Component {...props} />
			</ErrorBoundary>
		)
	}
}

// Guard all pages
const guardedPages = {
	WalletPage: withNavigationGuard(WalletPage),
	AdminCashToTokenPage: withNavigationGuard(
		AdminCashToTokenPage,
		UserRole.ADMIN
	),
	AdminTransactionsPage: withNavigationGuard(
		AdminTransactionsPage,
		UserRole.ADMIN
	),
	AdminMembershipsPage: withNavigationGuard(
		AdminMembershipsPage,
		UserRole.ADMIN
	),
	AdminVestingManagement: withNavigationGuard(
		AdminVestingManagementPage,
		UserRole.ADMIN
	),
	MenuPage: withNavigationGuard(MenuPage),
	TransactionsPage: withNavigationGuard(TransactionsPage),
	SwapPage: withNavigationGuard(SwapPage),
	BuyTokensPage: withNavigationGuard(BuyTokensPage),
	WaiverPage: withNavigationGuard(WaiverPage),
	MembershipPage: withNavigationGuard(MembershipPage),
	EntryPassesPage: withNavigationGuard(EntryPassesPage),
	BanxaCheckoutPage: withNavigationGuard(BanxaCheckoutPage),
	NotificationsPage: withNavigationGuard(NotificationsPage),
	EventsPage: withNavigationGuard(EventsPage),
	NewsPage: withNavigationGuard(NewsPage),
	GalleriesPage: withNavigationGuard(GalleriesPage),
	ShowEventPage: withNavigationGuard(ShowEventPage),
	ShowNewsPage: withNavigationGuard(ShowNewsPage),
	ShowGalleryPage: withNavigationGuard(ShowGalleryPage),
	AccountPage: withNavigationGuard(AccountPage)
}

// Enhanced app initialization
const App = () => (
	<StrictMode>
		<Router>
			<Switch>
				{/* Clean pages without providers */}
				<Route path="/terms-of-use" component={TermsPage}/>
				<Route path="/privacy-policy" component={PrivacyPolicyPage}/>
				<Route path="/change-email-confirm" component={AuthActionsPage}/>

				<Route>
					<ErrorBoundary>
						<AppKitProvider>
							<MobileProvider>
								<LoadingProvider>
									<UserProvider>
										<SWRConfig
											value={{
												fetcher,
												revalidateOnFocus: false,
												revalidateIfStale: false,
												dedupingInterval: 3000,
												errorRetryCount: 2,
												loadingTimeout: 5000,
												onError: (error) => {
													console.error('SWR Error:', error)
												},
												shouldRetryOnError: false
											}}>
											<Switch>
												<Route path="/" component={guardedPages.WalletPage}/>

												<Route
													path="/admin/cash-to-token"
													component={guardedPages.AdminCashToTokenPage}
												/>
												<Route
													path="/admin/transactions"
													component={guardedPages.AdminTransactionsPage}
												/>
												<Route
													path="/admin/memberships"
													component={guardedPages.AdminMembershipsPage}
												/>
												<Route
													path="/admin/vesting"
													component={guardedPages.AdminVestingManagement}
												/>

												<Route path="/menu" component={guardedPages.MenuPage}/>
												<Route path="/login" component={LoginPage}/>
												<Route path="/signup" component={SignupPage}/>
												<Route
													path="/forgot-password"
													component={ForgotPasswordPage}
												/>
												<Route
													path="/transactions"
													component={guardedPages.TransactionsPage}
												/>
												{/*<Route path="/swap" component={guardedPages.SwapPage} />*/}
												<Route
													path="/buy-tokens"
													component={guardedPages.BuyTokensPage}
												/>
												<Route
													path="/banxa-checkout"
													component={guardedPages.BanxaCheckoutPage}
												/>
												<Route
													path="/waivers"
													component={guardedPages.WaiverPage}
												/>
												<Route
													path="/membership"
													component={guardedPages.MembershipPage}
												/>
												<Route
													path="/entry-passes"
													component={guardedPages.EntryPassesPage}
												/>
												<Route
													path="/notifications"
													component={guardedPages.NotificationsPage}
												/>
												<Route
													path="/events/:id"
													component={guardedPages.ShowEventPage}
												/>
												<Route
													path="/events"
													component={guardedPages.EventsPage}
												/>
												<Route
													path="/news/:id"
													component={guardedPages.ShowNewsPage}
												/>
												<Route
													path="/news"
													component={guardedPages.NewsPage}
												/>
												<Route
													path="/galleries/:id"
													component={guardedPages.ShowGalleryPage}
												/>
												<Route
													path="/galleries"
													component={guardedPages.GalleriesPage}
												/>
												<Route
													path="/account"
													component={guardedPages.AccountPage}
												/>
												<Route component={NotFound}/>
											</Switch>
											<ChatSupport/>
											<Toaster/>
										</SWRConfig>
									</UserProvider>
								</LoadingProvider>
							</MobileProvider>
						</AppKitProvider>
					</ErrorBoundary>
				</Route>
			</Switch>
		</Router>
	</StrictMode>
)

export default App